import React, { useState, useCallback, useRef, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';
import { SHOP_DETAIL_ORIGIN } from '@shopDetails/tracking/actions/trackReadMoreClicked';
import { TextContainer, ToggleReadMore } from './components';
import connect from './connect';
import messages from './messages';

function ReadMore({
  description,
  lineHeight,
  maxLines,
  intl: { formatMessage },
  productId,
  index,
  trackReadMoreClicked,
}) {
  const ref = useRef();
  const [isReadMore, setIsReadMore] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const [maxHeight, setMaxHeight] = useState(false);

  useEffect(() => {
    const { current } = ref;
    setMaxHeight(current?.clientHeight);
    const lines = current?.clientHeight / lineHeight;

    if (lines > maxLines) {
      setIsReadMore(true);
      setShowReadMore(true);
    } else {
      setShowReadMore(false);
    }
  }, [description, lineHeight, maxLines]);

  const toggleReadMore = useCallback(
    e => {
      e.stopPropagation();
      trackReadMoreClicked({
        isReadMore,
        position: index,
        productSku: productId,
        screenType: SHOP_DETAIL_ORIGIN,
      });
      setIsReadMore(prev => !prev);
    },
    [trackReadMoreClicked, index, productId, isReadMore],
  );

  return (
    <div>
      <TextContainer
        data-testid="read-more-container"
        ref={ref}
        withMaxLines={maxLines}
        withReadMore={isReadMore}
        withLineHeight={lineHeight}
        withMaxHeight={maxHeight}
      >
        {description}
      </TextContainer>
      {showReadMore && (
        <ToggleReadMore
          aria-hidden="true"
          onClick={toggleReadMore}
          withReadMore={isReadMore}
          withLineHeight={lineHeight}
        >
          {formatMessage(isReadMore ? messages.readMore : messages.readLess)}
        </ToggleReadMore>
      )}
    </div>
  );
}

ReadMore.propTypes = {
  description: PropTypes.string.isRequired,
  lineHeight: PropTypes.number,
  maxLines: PropTypes.number,
  intl: intlShape.isRequired,
  productId: PropTypes.number,
  index: PropTypes.number,
  trackReadMoreClicked: PropTypes.func.isRequired,
};

ReadMore.defaultProps = {
  lineHeight: 16,
  maxLines: 2,
};

export default memo(connect(injectIntl(ReadMore)));
